import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Sprout, Heart, Users, Target, Calendar, ArrowRight, Check, Sparkles, Clock, FolderOpen, Wallet, MessageSquare, ShieldCheck, Bot, Download, FileText, UserRoundCheck, ShieldAlert } from "lucide-react";

const LOGO_URL = "/thrively-logo.png";

const features = [
  { icon: Sprout, title: "Goals & Activities", desc: "Track developmental milestones and daily therapy tasks" },
  { icon: Calendar, title: "Unified Calendar", desc: "Appointments, goal target dates and activity due dates in one view" },
  { icon: FolderOpen, title: "Document Management", desc: "Securely store IEPs, assessments and therapy reports" },
  { icon: Wallet, title: "Budget Tracking", desc: "Allocate funds, log expenses, get notified as limits approach" },
  { icon: MessageSquare, title: "Community", desc: "Share, discuss and exchange resources with other families" },
  { icon: ShieldCheck, title: "Privacy First", desc: "HIPAA-grade security, encryption and audit logging" },
  { icon: Bot, title: "AI Smarts", desc: "Summarize saved articles and extract activities from session notes" },
  { icon: FileText, title: "Smarter Session Notes", desc: "Write in Markdown, import PDFs privately in your browser, and turn observations into next steps" },
  { icon: UserRoundCheck, title: "Connected Care Team", desc: "Keep child profiles, carers, and therapists together so everyone has the right context" },
  { icon: ShieldAlert, title: "Safer Community", desc: "Share support with reactions, reports, and moderator tools that protect the conversation" },
  { icon: Download, title: "Export Everything", desc: "Download your child's full record as CSV whenever you want" },
];

export default function LandingPage() {
  const { login, register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", password: "" });
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const scrollToSection = async (event, sectionId) => {
    event.preventDefault();
    const content = document.querySelector(".public-scroll-content");
    const target = document.getElementById(sectionId);
    if (!content || !target || typeof content.getScrollElement !== "function") {
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const scrollElement = await content.getScrollElement();
    const top = target.getBoundingClientRect().top - scrollElement.getBoundingClientRect().top + scrollElement.scrollTop - 24;
    content.scrollToPoint(0, top, 300);
  };

  // Redirect if authenticated using useEffect to avoid setState during render
  if (isAuthenticated) {
    window.location.href = "/overview";
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      navigate("/overview");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerForm.name, registerForm.email, registerForm.password);
      toast.success("Account created successfully!");
      navigate("/overview");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    }
    setLoading(false);
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setWaitlistLoading(true);
    try {
      await axios.post(`${API}/waitlist/premium`, { email: waitlistEmail });
      setWaitlistSubmitted(true);
      toast.success("You're on the Premium waitlist!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not join waitlist. Please try again.");
    }
    setWaitlistLoading(false);
  };

  const handleGoogleSignIn = async () => {
    // Client ID comes from the server at runtime (GOOGLE_CLIENT_ID); a build-time value still wins.
    let clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      try {
        clientId = (await axios.get(`${API}/config`)).data.google_client_id;
      } catch {
        clientId = null;
      }
    }
    if (!clientId) {
      toast.error("Google sign-in is not configured");
      return;
    }
    const redirectUri = window.location.origin + '/auth/google';
    const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('google_oauth_state', state);
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent('openid email profile')}` +
      `&state=${encodeURIComponent(state)}` +
      `&access_type=online` +
      `&prompt=select_account`;
    window.location.href = authUrl;
  };

  return (
    <div className="min-h-screen gradient-warm">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Thrively" className="h-20 w-auto" />
            </div>
            <nav className="flex items-center gap-6">
              <a
                href="#help"
                onClick={(event) => scrollToSection(event, "help")}
                className="hidden sm:inline text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                Help
              </a>
              <a
                href="#pricing"
                onClick={(event) => scrollToSection(event, "pricing")}
                className="hidden sm:inline text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                data-testid="nav-pricing-link"
              >
                Pricing
              </a>
              <a
                href="#auth"
                onClick={(event) => scrollToSection(event, "auth")}
                className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
              >
                Sign In
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fadeIn">
              <div className="inline-flex items-center gap-2 bg-golden-100 text-golden-500 px-4 py-2 rounded-full text-sm font-medium">
                <Heart className="h-4 w-4" />
                Supporting Neurodiverse Families
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-primary tracking-tight leading-tight">
                Empower Your Child's
                <span className="text-secondary"> Growth Journey</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
                Thrively helps parents of neurodiverse children coordinate therapy, 
                track progress, and celebrate every milestone. All in one nurturing space.
              </p>
              <p className="text-sm text-muted-foreground max-w-xl">
                Start by creating a free account, then add your child and build the care plan that works for your family.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="#auth" onClick={(event) => scrollToSection(event, "auth")}>
                  <Button 
                    size="lg" 
                    className="rounded-full px-8 shadow-lg hover:shadow-xl transition-all btn-scale"
                    data-testid="get-started-btn"
                  >
                    Get Started Free
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </div>

              {/* Trust badges */}
              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-5 w-5 text-success" />
                  HIPAA Compliant
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-5 w-5 text-success" />
                  Free to Start
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-5 w-5 text-success" />
                  Family-First
                </div>
              </div>
            </div>

            {/* Right - Hero Image */}
            <div className="relative animate-fadeIn stagger-2">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="/hero.jpg"
                  alt="Parent and child"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
              </div>
              
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl animate-slideIn stagger-3">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-success/10 flex items-center justify-center">
                    <Target className="h-6 w-6 text-success" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-lg">85%</p>
                    <p className="text-xs text-muted-foreground">Goals Achieved</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Everything You Need
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Designed by parents, for parents. Manage your child's therapy journey with ease.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <Card 
                key={feature.title} 
                className={`border-0 shadow-lg hover:shadow-xl transition-all duration-300 rounded-3xl card-hover animate-fadeIn stagger-${(i % 5) + 1}`}
              >
                <CardContent className="p-6">
                  <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                    <feature.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section id="help" className="py-16 px-4 sm:px-6 lg:px-8 bg-primary/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">Getting Started Help</h2>
            <p className="text-muted-foreground">Use these quick steps to turn care details into an organized, shared plan.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ["1. Build your team", "Add a child, the carers involved in daily care, and their therapists."],
              ["2. Plan the journey", "Set goals, schedule appointments, and create activities that support progress."],
              ["3. Keep everything connected", "Save documents and session notes, track budgets, and use the calendar to stay ahead."]
            ].map(([title, description]) => (
              <Card key={title} className="border-0 shadow-lg rounded-3xl">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-bold text-primary">{title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-primary/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Simple, Family-Friendly Pricing
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Start free. Upgrade when your family is ready for more.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Free Tier */}
            <Card className="border-0 shadow-lg rounded-3xl card-hover animate-fadeIn stagger-1" data-testid="pricing-free-tier">
              <CardContent className="p-8 flex flex-col h-full">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-success/10 text-success text-xs font-semibold rounded-full">FREE FOREVER</span>
                  </div>
                  <h3 className="font-heading text-2xl font-bold mb-2">Family Starter</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-heading font-bold">$0</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">Everything you need to get going</p>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                    <span><strong>1 child profile</strong></span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                    <span><strong>2 therapy providers</strong></span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                    <span><strong>2 goals</strong> · <strong>10 session notes</strong> · <strong>10 activities</strong></span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                    <span><strong>1 budget</strong> with expense tracking</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                    <span><strong>5 documents</strong> (10 MB each)</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                    <span>Full appointment calendar</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                    <span>Community forum access</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                    <span>HIPAA-grade security & audit logs</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                    <span>CSV data export</span>
                  </li>
                </ul>

                <a href="#auth" onClick={(event) => scrollToSection(event, "auth")}>
                  <Button variant="outline" className="w-full rounded-full" data-testid="pricing-free-cta">
                    Get Started Free
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Premium Tier */}
            <Card className="border-2 border-primary shadow-2xl rounded-3xl card-hover relative overflow-hidden animate-fadeIn stagger-2" data-testid="pricing-premium-tier">
              <div className="absolute top-0 right-0 bg-gradient-to-l from-primary to-primary/70 text-white text-xs font-bold px-4 py-1.5 rounded-bl-2xl flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" />
                COMING SOON
              </div>
              <CardContent className="p-8 flex flex-col h-full">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full">PREMIUM</span>
                  </div>
                  <h3 className="font-heading text-2xl font-bold mb-2 flex items-center gap-2">
                    Family Plus
                  </h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-heading font-bold text-primary">TBD</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">For larger or more complex care journeys</p>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Unlimited</strong> children, providers, goals, session notes, activities, budgets & documents</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Library</strong> — save articles from the web + community posts, read offline</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>AI article summaries</strong> in Library (bring your own key)</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>AI activity extraction</strong> from session notes</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Google Drive & Dropbox auto-sync</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Clock className="h-5 w-5 text-primary/60 flex-shrink-0 mt-0.5" />
                    <span>Multiple carer logins <span className="text-xs italic">(coming soon)</span></span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Clock className="h-5 w-5 text-primary/60 flex-shrink-0 mt-0.5" />
                    <span>Resource kits <span className="text-xs italic">(coming soon)</span></span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Clock className="h-5 w-5 text-primary/60 flex-shrink-0 mt-0.5" />
                    <span>Advanced analysis <span className="text-xs italic">(coming soon)</span></span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Everything in Family Starter</span>
                  </li>
                </ul>

                {/* Notify-me waitlist capture */}
                {waitlistSubmitted ? (
                  <div className="rounded-full bg-success/10 text-success text-sm font-medium py-3 px-4 text-center flex items-center justify-center gap-2" data-testid="waitlist-success">
                    <Check className="h-4 w-4" /> You're on the waitlist!
                  </div>
                ) : (
                  <form onSubmit={handleWaitlistSubmit} className="space-y-2" data-testid="waitlist-form">
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={waitlistEmail}
                        onChange={(e) => setWaitlistEmail(e.target.value)}
                        required
                        disabled={waitlistLoading}
                        className="rounded-full text-sm"
                        data-testid="waitlist-email-input"
                      />
                      <Button
                        type="submit"
                        disabled={waitlistLoading}
                        className="rounded-full shrink-0"
                        data-testid="waitlist-submit-btn"
                      >
                        <Sparkles className="mr-1.5 h-4 w-4" />
                        {waitlistLoading ? "..." : "Notify Me"}
                      </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground text-center">
                      Get an email the moment Premium launches. No spam.
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-8">
            Premium pricing and launch date will be announced soon. Free tier will always remain free.
          </p>
        </div>
      </section>

      {/* Auth Section */}
      <section id="auth" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden">
            <CardHeader className="text-center pb-2 pt-8">
              <img src={LOGO_URL} alt="Thrively" className="h-24 w-auto mx-auto mb-4" />
              <CardTitle className="font-heading text-2xl">Welcome to Thrively</CardTitle>
              <CardDescription>Start your journey today</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 rounded-full bg-muted p-1 mb-6">
                  <TabsTrigger 
                    value="login" 
                    className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow"
                    data-testid="login-tab"
                  >
                    Sign In
                  </TabsTrigger>
                  <TabsTrigger 
                    value="register" 
                    className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow"
                    data-testid="register-tab"
                  >
                    Sign Up
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@example.com"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        required
                        className="rounded-xl h-12"
                        data-testid="login-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        required
                        className="rounded-xl h-12"
                        data-testid="login-password"
                      />
                    </div>
                    <div className="text-right mt-2">
                      <a href="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</a>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full rounded-full h-12 mt-4" 
                      disabled={loading}
                      data-testid="login-submit"
                    >
                      {loading ? "Signing in..." : "Sign In"}
                    </Button>
                  </form>
                  <div className="relative my-5">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                    <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-muted-foreground">or</span></div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full rounded-full h-12 font-medium"
                    onClick={handleGoogleSignIn}
                    data-testid="google-signin-btn"
                  >
                    <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                    Continue with Google
                  </Button>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Full Name</Label>
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="Jane Doe"
                        value={registerForm.name}
                        onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                        required
                        className="rounded-xl h-12"
                        data-testid="register-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="you@example.com"
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                        required
                        className="rounded-xl h-12"
                        data-testid="register-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        required
                        className="rounded-xl h-12"
                        data-testid="register-password"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full rounded-full h-12 mt-6" 
                      disabled={loading}
                      data-testid="register-submit"
                    >
                      {loading ? "Creating account..." : "Create Account"}
                    </Button>
                  </form>
                  <div className="relative my-5">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                    <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-muted-foreground">or</span></div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full rounded-full h-12 font-medium"
                    onClick={handleGoogleSignIn}
                    data-testid="google-signup-btn"
                  >
                    <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                    Continue with Google
                  </Button>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-border bg-white/50">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Thrively" className="h-12 w-auto" />
          </div>
          <div className="flex items-center gap-4">
            <a href="/privacy-policy" className="text-sm text-muted-foreground hover:text-primary transition-colors">Privacy Policy</a>
            <span className="text-muted-foreground/30">|</span>
            <p className="text-sm text-muted-foreground">
              © 2026 Thrively. Supporting families, nurturing growth.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
